<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import classnames from 'classnames';
  import { omit } from '$lib/utils/object';
  // Default Prop
  export let group: boolean | string | number | null | undefined = undefined;
  export let value: boolean | string | number | undefined = undefined;
  export let disabled: boolean = false;
  export let compact: boolean = false;
  export let text: string | undefined = '';
  export let error: string | null = null;

  const dispatch = createEventDispatcher();

  const onInput = (event) => {
    dispatch("input", event);
  };
</script>

<label
  class={classnames('radio-button-label', $$props.class)}
  for={$$restProps.id || $$restProps.name}
  class:compact
>
  <input
    on:click
    on:input={onInput}
    id={$$restProps.id || $$restProps.name}
    type="radio"
    name={$$restProps.name}
    {value}
    bind:group
    {...$$restProps}
  />
  <span class="radio-circle" />
  <span class="radio-text">
    <slot />
  </span>

  {#if error}
    <span class="error">{error}</span>
  {/if}
</label>

<style type="text/scss" lang="scss" scoped>
  .error {
    @apply text-xs;
    @apply text-error;
  }

  /* label */
  .radio-button-label {
    @apply flex justify-start flex-nowrap;
    @apply relative;
  }

  /* input */
  input[type='radio'] {
    @apply absolute;
    clip: rect(0, 0, 0, 0);
  }

  /* .design */
  .radio-circle {
    @apply w-5 h-5;
    @apply flex-shrink-0;
    @apply border-2 border-gray-600 border-opacity-50 rounded-full;
    @apply relative;
    @apply ease-in-out duration-200;
    transition-property: border-color, opacity;
  }

  .radio-circle.square {
    border-radius: 0 !important;
  }

  input[type='radio']:not(:disabled) ~ .radio-circle {
    @apply cursor-pointer;
  }

  /* Checked/Hover */
  input[type='radio']:not(:disabled):checked + .radio-circle,
  input[type='radio']:not(:disabled):hover + .radio-circle,
  input[type='radio']:not(:disabled):focus + .radio-circle {
    @apply border-gray-900;
  }

  /* Focus */
  input[type='radio']:not(:disabled):focus + .radio-circle {
    @apply ring-2 ring-offset-2 ring-brand ring-opacity-50;
  }

  .radio-circle + .radio-text {
    @apply ml-4;
  }

  .radio-button-label.compact .radio-circle + .radio-text {
    @apply ml-2;
  }

  .radio-text {
    @apply flex-1 items-start;
    @apply text-gray-900 font-sans text-sm;
    @apply ease-in-out duration-200;
    transition-property: opacity;
  }

  .radio-circle::before,
  .radio-circle::after {
    @apply absolute scale-x-0 origin-center;
    /* Offset the circle because of border of the parent */
    @apply -top-0.5 -left-0.5;
    content: '';
    width: inherit;
    height: inherit;
    border-radius: inherit;
  }

  /* MAIN CIRCLE
  visibile in the checked state */
  .radio-circle:before {
    @apply ease-in-out duration-200;
    @apply bg-gray-900;
    @apply opacity-0;
    @apply transform scale-0;
    transition-property: background-color, transform, opacity;
  }

  /* => Checked */
  input[type='radio']:checked + .radio-circle::before {
    @apply opacity-100 scale-50;
  }

  /* Ripple effect pseudo-element */
  .radio-circle::after {
    @apply ease-in-out duration-200;
    @apply bg-brand;
    @apply opacity-0;
    transition-property: transform, opacity;
  }

  input[type='radio']:checked + .radio-circle::after {
    @apply animate-ripple-effect;
  }

  /* DISABLED */
  input[type='radio']:disabled + .radio-circle,
  input[type='radio']:disabled ~ .radio-text {
    @apply cursor-default;
    @apply opacity-50;
  }
</style>
